import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from './supabase'
import i18n from './i18n'
import Auth from './components/Auth'
import Dashboard from './components/Dashboard'
import OwnerDashboard from './components/OwnerDashboard'
import BetaCommunity, { BetaCommunityGate } from './components/BetaCommunity'
import ErrorBoundary from './components/shared/ErrorBoundary'
import InstallPrompt from './components/InstallPrompt'
import PrivacyPolicy from './components/PrivacyPolicy'
import TermsOfService from './components/TermsOfService'
import NDAPage from './components/NDAPage'
import InviteAccept from './components/InviteAccept'
import PublicHomeConnect from './components/PublicHomeConnect'
import ContractorJoin from './components/ContractorJoin'
import GetUnstuckButton from './components/GetUnstuckButton'
import Settings from './components/Settings'
import HealthVaultDemo from './components/dev/HealthVaultDemo'
import UsageStats from './components/dev/UsageStats'
import OwnerDataPlates from './components/OwnerDataPlates'
import AITestBench from './components/AITestBench'
import { sweepPendingVideoUploads } from './utils/videoBackgroundUpload'
import BrowserTranslateBanner from './components/BrowserTranslateBanner'
import { TourProvider } from './tour/TourContext'
import TourOverlay from './tour/components/TourOverlay'
import ReferralLanding from './components/ReferralLanding'

const OWNER_ID = '26680bcf-f67b-490e-ba08-e1f25ddcfd01'

function getInviteToken() {
  const m = window.location.pathname.match(/^\/invite\/([a-f0-9]+)$/)
  return m ? m[1] : null
}

function App() {
  const { i18n } = useTranslation()
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [inviteToken] = useState(() => getInviteToken())
  const [currentPage, setCurrentPage] = useState(() => {
    const p = window.location.pathname.replace(/\/$/, '')
    if (p === '/owner/data-plates') return 'owner_data_plates'
    if (p === '/owner/ai-test-bench') return 'owner_ai_test_bench'
    if (p === '/owner') return 'owner'
    if (p === '/beta') return 'beta'
    if (p === '/privacy') return 'privacy'
    if (p === '/terms') return 'terms'
    if (p === '/nda') return 'nda'
    if (/^\/invite\/[a-f0-9]+$/.test(p)) return 'invite'
    if (p === '/join') return 'join'
    if (p === '/contractors/join') return 'contractor_join'
    if (/^\/connect\/[a-z0-9]+$/i.test(p)) return 'public_connect'
    if (/^\/r\/[a-zA-Z0-9_-]+$/.test(p)) return 'referral'
    if (p === '/settings') return 'settings'
    if (p === '/dev/health-vault') return 'dev_health_vault'
    if (p === '/dev/usage-stats') return 'dev_usage_stats'
    return 'dashboard'
  })

  useEffect(() => {
    document.documentElement.lang = i18n.language || 'en'
    const handleChange = (lng) => {
      document.documentElement.lang = lng || 'en'
    }
    i18n.on('languageChanged', handleChange)
    return () => i18n.off('languageChanged', handleChange)
  }, [i18n])

  // Diagnostic-only sweep: surfaces any raw walkthrough videos that
  // failed their post-scan background upload in a previous session.
  // No retry — the Blob isn't persisted across reloads.
  useEffect(() => {
    sweepPendingVideoUploads()
  }, [])

  // Read path once on mount — catches any case where initializer ran too early
  useEffect(() => {
    const p = window.location.pathname.replace(/\/$/, '')
    if (p === '/owner/data-plates') setCurrentPage('owner_data_plates')
    else if (p === '/owner/ai-test-bench') setCurrentPage('owner_ai_test_bench')
    else if (p === '/owner') setCurrentPage('owner')
    else if (p === '/beta') setCurrentPage('beta')
    else if (p === '/privacy') setCurrentPage('privacy')
    else if (p === '/terms') setCurrentPage('terms')
    else if (p === '/nda') setCurrentPage('nda')
    else if (/^\/invite\/[a-f0-9]+$/.test(p)) setCurrentPage('invite')
    else if (p === '/join') setCurrentPage('join')
    else if (p === '/contractors/join') setCurrentPage('contractor_join')
    else if (/^\/connect\/[a-z0-9]+$/i.test(p)) setCurrentPage('public_connect')
    else if (/^\/r\/[a-zA-Z0-9_-]+$/.test(p)) setCurrentPage('referral')
    else if (p === '/settings') setCurrentPage('settings')
    else if (p === '/dev/health-vault') setCurrentPage('dev_health_vault')
  }, [])

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setLoading(false)
      if (session?.user?.id) loadLanguagePreference(session.user.id)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session)
        if (session?.user?.id) loadLanguagePreference(session.user.id)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  async function loadLanguagePreference(userId) {
    const { data } = await supabase.from('profiles')
      .select('language_preference')
      .eq('id', userId)
      .maybeSingle()
    if (data?.language_preference) {
      i18n.changeLanguage(data.language_preference)
      localStorage.setItem('casavio_preferred_language', data.language_preference)
    }
  }

  if (currentPage === 'beta') return <BetaCommunityGate supabase={supabase} session={session} />
  if (currentPage === 'privacy') return <PrivacyPolicy />
  if (currentPage === 'terms') return <TermsOfService />
  if (currentPage === 'nda') return <NDAPage />

  // Old referral links (/join?ref=code) → canonical /r/:code, preserving the
  // code on the hop. Only redirects when /join carries a ref param; bare /join
  // (used for sign-in and other flows) is left untouched.
  if (currentPage === 'join') {
    const ref = new URLSearchParams(window.location.search).get('ref')
    if (ref) { window.location.replace(`/r/${encodeURIComponent(ref)}`); return null }
  }

  if (loading) return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: '#F2EDE4',
      fontFamily: 'Georgia, serif', fontSize: '20px', color: '#B8721F'
    }}>
      Loading Casavio...
    </div>
  )

  if (currentPage === 'referral') {
    const code = window.location.pathname.replace(/^\/r\//, '').replace(/\/$/, '')
    return <ReferralLanding code={code} session={session} />
  }

  if (currentPage === 'settings') {
    if (!session?.user) return <><Auth /><InstallPrompt /></>
    return (
      <ErrorBoundary>
        <Settings session={session} onBack={() => { window.location.href = '/' }} />
        <GetUnstuckButton />
      </ErrorBoundary>
    )
  }

  if (currentPage === 'join') return <><Auth /><InstallPrompt /></>
  if (currentPage === 'contractor_join') return <ContractorJoin />
  if (currentPage === 'invite') return <InviteAccept token={inviteToken} session={session} />
  if (currentPage === 'public_connect') {
    const connectToken = window.location.pathname.replace(/^\/connect\//, '')
    return <PublicHomeConnect token={connectToken} />
  }

  if (currentPage === 'owner') {
    if (!session?.user) return <><Auth /><InstallPrompt /></>
    if (session.user.id !== OWNER_ID) return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', background: '#F2EDE4',
        fontFamily: 'Georgia, serif', fontSize: '18px', color: '#B03020'
      }}>
        Access denied.
      </div>
    )
    return <ErrorBoundary><OwnerDashboard session={session} /><InstallPrompt /><GetUnstuckButton /></ErrorBoundary>
  }

  if (currentPage === 'owner_data_plates') {
    if (!session?.user) return <><Auth /><InstallPrompt /></>
    if (session.user.id !== OWNER_ID) {
      window.location.replace('/')
      return null
    }
    return <ErrorBoundary><OwnerDataPlates session={session} /><InstallPrompt /><GetUnstuckButton /></ErrorBoundary>
  }

  if (currentPage === 'owner_ai_test_bench') {
    if (!session?.user) return <><Auth /><InstallPrompt /></>
    if (session.user.id !== OWNER_ID) {
      window.location.replace('/')
      return null
    }
    return <ErrorBoundary><AITestBench session={session} /><InstallPrompt /><GetUnstuckButton /></ErrorBoundary>
  }

  if (currentPage === 'dev_health_vault') {
    if (!session?.user) return <><Auth /><InstallPrompt /></>
    if (session.user.id !== OWNER_ID) {
      window.location.replace('/')
      return null
    }
    return <ErrorBoundary><HealthVaultDemo session={session} /></ErrorBoundary>
  }

  if (currentPage === 'dev_usage_stats') {
    if (!session?.user) return <><Auth /><InstallPrompt /></>
    if (session.user.id !== OWNER_ID) {
      window.location.replace('/')
      return null
    }
    return <ErrorBoundary><UsageStats session={session} /></ErrorBoundary>
  }

  return (
    <TourProvider session={session}>
      <BrowserTranslateBanner />
      <ErrorBoundary>
        {session?.user ? <Dashboard session={session} /> : <Auth />}
        <InstallPrompt />
        {session?.user && <GetUnstuckButton />}
      </ErrorBoundary>
      <TourOverlay />
    </TourProvider>
  )
}

export default App
